/**
 * Источник правды об активном тенанте на фронте админки.
 *
 * Хранит slug активного тенанта в памяти + localStorage (`preferredTenant`).
 * Читается СИНХРОННО из fetch-интерцептора (`fetch-with-auth.ts`), чтобы
 * проставлять заголовок `X-Tenant-Id` на каждый API-запрос без обращения к React-контексту.
 *
 * Однотенантный режим (backend не присылает `tenant` в /auth/admin/me):
 * активный slug = null → заголовок НЕ добавляется, поведение байт-в-байт как раньше.
 */

const STORAGE_KEY = 'preferredTenant';
// Кэш последнего известного списка БД + активной — чтобы переключатель не пропадал
// при рефреше, пока асинхронно грузится /auth/admin/me.
const TENANTS_CACHE_KEY = 'tenantsCache';

export interface CachedTenantSummary {
  slug: string;
  name: string;
  logoUrl: string | null;
}

export interface CachedTenantState {
  activeSlug: string | null;
  tenants: CachedTenantSummary[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseCachedSummary(value: unknown): CachedTenantSummary | null {
  if (!isRecord(value) || typeof value.slug !== 'string' || value.slug.length === 0) {
    return null;
  }
  return {
    slug: value.slug,
    name: typeof value.name === 'string' && value.name.length > 0 ? value.name : value.slug,
    logoUrl: typeof value.logoUrl === 'string' ? value.logoUrl : null,
  };
}

/** Прочитать закэшированное состояние тенантов (для мгновенного рендера до /me). */
export function readCachedTenantState(): CachedTenantState {
  try {
    const raw = localStorage.getItem(TENANTS_CACHE_KEY);
    if (!raw) {
      return { activeSlug: null, tenants: [] };
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return { activeSlug: null, tenants: [] };
    }
    const tenants: CachedTenantSummary[] = [];
    if (Array.isArray(parsed.tenants)) {
      for (const entry of parsed.tenants) {
        const summary = parseCachedSummary(entry);
        if (summary) {
          tenants.push(summary);
        }
      }
    }
    const activeSlug = typeof parsed.activeSlug === 'string' ? parsed.activeSlug : null;
    return { activeSlug, tenants };
  } catch {
    return { activeSlug: null, tenants: [] };
  }
}

/** Сохранить состояние тенантов в кэш. */
export function writeCachedTenantState(state: CachedTenantState): void {
  try {
    localStorage.setItem(TENANTS_CACHE_KEY, JSON.stringify(state));
  } catch {
    // localStorage недоступен — не критично
  }
}

/**
 * Активный slug сидируется из claim `tenant` JWT-токена (авторитетный источник,
 * сверяется backend'ом). В однотенантном режиме backend не кладёт claim → null,
 * заголовок X-Tenant-Id не добавляется, поведение идентично до-tenant версии.
 * Сидирование из JWT (а не из localStorage) исключает отправку устаревшего
 * X-Tenant-Id после reload до ответа /auth/admin/me.
 */
let activeTenantSlug: string | null = readTenantFromToken();

function readTenantFromToken(): string | null {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return null;
    }
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payloadRaw = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload: unknown = JSON.parse(payloadRaw);
    if (
      typeof payload === 'object' &&
      payload !== null &&
      'tenant' in payload &&
      typeof payload.tenant === 'string' &&
      payload.tenant.length > 0
    ) {
      return payload.tenant;
    }
    return null;
  } catch {
    return null;
  }
}

function readPreferredTenant(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

/**
 * Текущий активный tenant slug (или null в однотенантном режиме).
 *
 * JWT claim — авторитетный источник после login/refresh/switch: in-memory
 * `activeTenantSlug` сидируется только на загрузке модуля и через setActiveTenant,
 * поэтому сразу после login (токен уже в localStorage, память ещё null) без
 * чтения JWT интерцептор не ставил X-Tenant-Id → backend default-тенант → 404.
 */
export function getActiveTenant(): string | null {
  return readTenantFromToken() ?? activeTenantSlug;
}

/**
 * Перечитать active tenant из accessToken (вызывать сразу после login/refresh,
 * когда JWT уже записан, а in-memory slug ещё не обновлён).
 */
export function seedActiveTenantFromToken(): string | null {
  const slug = readTenantFromToken();
  if (slug) {
    setActiveTenant(slug);
  }
  return slug;
}

/**
 * Подсказка предпочтительного тенанта из localStorage — отправляется в теле login,
 * чтобы backend выбрал ту же активную БД, что админ выбирал в прошлый раз.
 */
export function getPreferredTenant(): string | null {
  return readPreferredTenant();
}

/**
 * Установить активный tenant. `null` — сбросить (однотенантный режим / logout).
 */
export function setActiveTenant(slug: string | null): void {
  activeTenantSlug = slug && slug.length > 0 ? slug : null;

  try {
    if (activeTenantSlug) {
      localStorage.setItem(STORAGE_KEY, activeTenantSlug);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage недоступен — работаем только из памяти
  }
}

/**
 * Очистить активный tenant (вызывается при logout).
 */
export function clearActiveTenant(): void {
  activeTenantSlug = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TENANTS_CACHE_KEY);
  } catch {
    // ignore
  }
}
